/**
 * 健壮 JSON 解析器 — 专治 AI 生成的大体量 JSON
 * ──────────────────────────────────────────────────────────────────────────
 * 痛点：1000 章大纲解析时，AI 单次响应的 subPlots/foreshadowings 数组太大，
 *       经常在 max_tokens 上限处被截断，导致 JSON.parse 失败。
 *
 * 本工具分多级策略逐层降级，尽可能从残缺/带噪文本中救回有效数据：
 *   L1 平衡括号提取 → L2 尾逗号修复 → L3 截断自动闭合 → L4 字符串内未转义换行修复
 *   → L5 逐对象提取（数组场景的终极兜底）
 *
 * 全部纯字符串操作，无第三方依赖。
 */

/**
 * 从混合文本中提取第一个平衡的 {...} JSON 块。
 * 逐字符扫描，正确处理字符串内的 {} 和转义，不依赖贪婪正则。
 * @param {string} text AI 原始返回（可能含 ```json 代码块、寒暄前言等）
 * @returns {{json: string, balanced: boolean}} json=提取的文本，balanced=是否找到完整闭合
 */
function extractBalancedJson(text) {
  if (!text || typeof text !== 'string') return { json: '', balanced: false };

  // 去掉 ```json / ``` 代码块围栏
  const fenceMatch = text.match(/```(?:json)?\s*\n?([\s\S]*?)```/);
  let src = fenceMatch ? fenceMatch[1] : text;

  const start = src.indexOf('{');
  if (start === -1) return { json: '', balanced: false };

  let depth = 0;
  let inStr = false;
  let esc = false;
  let lastClose = -1;

  for (let i = start; i < src.length; i++) {
    const ch = src[i];
    if (esc) { esc = false; continue; }
    if (ch === '\\') { esc = true; continue; }
    if (ch === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      lastClose = i;
      if (depth === 0) return { json: src.slice(start, i + 1), balanced: true };
    }
  }
  // 截断：未闭合，返回从头到最后一个 } 的内容（交由后续修复尝试闭合）
  if (lastClose > start) return { json: src.slice(start, lastClose + 1), balanced: false };
  return { json: src.slice(start), balanced: false };
}

/**
 * 修复尾逗号：删除 ] 和 } 前面的逗号（AI 常在数组/对象末元素后多写逗号）
 */
function fixTrailingCommas(s) {
  return s.replace(/,(\s*[}\]])/g, '$1');
}

/**
 * 修复未转义的控制字符：JSON 字符串值内不允许裸换行（\n）和裸回车（\r）和裸 Tab。
 * AI 生成长文本时经常在 description 里直接换行而不转义。
 * 只修复字符串值内部的，不动结构。
 */
function fixUnescapedControlChars(s) {
  let out = '';
  let inStr = false;
  let esc = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (esc) { out += ch; esc = false; continue; }
    if (ch === '\\') { out += ch; esc = true; continue; }
    if (ch === '"') { inStr = !inStr; out += ch; continue; }
    if (inStr) {
      if (ch === '\n') { out += '\\n'; continue; }
      if (ch === '\r') { out += '\\r'; continue; }
      if (ch === '\t') { out += '\\t'; continue; }
    }
    out += ch;
  }
  return out;
}

/**
 * 截断修复：统计未闭合的 { 和 [，在末尾补上对应的闭合符。
 * 用于 AI 输出被 max_tokens 截断、JSON 残缺的场景。
 */
function closeUnclosedBrackets(s) {
  const stack = [];
  let inStr = false;
  let esc = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (esc) { esc = false; continue; }
    if (ch === '\\') { esc = true; continue; }
    if (ch === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (ch === '{' || ch === '[') stack.push(ch);
    else if (ch === '}' && stack[stack.length - 1] === '{') stack.pop();
    else if (ch === ']' && stack[stack.length - 1] === '[') stack.pop();
  }
  let suffix = '';
  while (stack.length > 0) {
    const opener = stack.pop();
    suffix += (opener === '{') ? '}' : ']';
  }
  return s + suffix;
}

/**
 * 终极兜底：从残缺的 JSON 文本中逐个提取完整的 {...} 对象。
 * 用于数组被截断、部分对象残缺的场景——只要能找到完整的单个对象就提取。
 * @param {string} text
 * @param {string} arrayKey 数组在 JSON 中的键名（用于返回归类）
 * @returns {Array} 提取到的完整对象数组
 */
function extractIndividualObjects(text) {
  const objs = [];
  let depth = 0;
  let inStr = false;
  let esc = false;
  let objStart = -1;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (esc) { esc = false; continue; }
    if (ch === '\\') { esc = true; continue; }
    if (ch === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (ch === '{') {
      if (depth === 0) objStart = i;
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0 && objStart !== -1) {
        const frag = text.slice(objStart, i + 1);
        try {
          const parsed = JSON.parse(fixTrailingCommas(frag));
          objs.push(parsed);
        } catch (_) {
          // 单个对象也解析不了就跳过
        }
        objStart = -1;
      }
    }
  }
  return objs;
}

/**
 * 主函数：多级降级解析 AI 返回的（可能残缺/带噪的）JSON 文本。
 * @param {string} rawText AI 返回的原始内容
 * @param {{expectArrayKey?: string}} [opts] opts.expectArrayKey=当整体解析失败时，尝试从文本提取该键对应的数组里的独立对象
 * @returns {{ok: boolean, data: any|null, fragments: Array, strategy: string}}
 */
function robustJsonParse(rawText, opts = {}) {
  if (!rawText || typeof rawText !== 'string') {
    return { ok: false, data: null, fragments: [], strategy: 'empty-input' };
  }

  const { json: extracted } = extractBalancedJson(rawText);
  if (!extracted) {
    return { ok: false, data: null, fragments: [], strategy: 'no-json-found' };
  }

  // L1：直接解析
  try {
    return { ok: true, data: JSON.parse(extracted), fragments: [], strategy: 'direct' };
  } catch (_) {}

  // L2：修复尾逗号
  const l2 = fixTrailingCommas(extracted);
  try {
    return { ok: true, data: JSON.parse(l2), fragments: [], strategy: 'trailing-comma' };
  } catch (_) {}

  // L3：修复未转义控制字符（字符串内裸换行）+ 尾逗号
  const l3 = fixTrailingCommas(fixUnescapedControlChars(extracted));
  try {
    return { ok: true, data: JSON.parse(l3), fragments: [], strategy: 'control-char' };
  } catch (_) {}

  // L4：截断自动闭合 + 尾逗号（处理 max_tokens 截断）
  const l4 = fixTrailingCommas(closeUnclosedBrackets(extracted));
  try {
    return { ok: true, data: JSON.parse(l4), fragments: [], strategy: 'truncation-close' };
  } catch (_) {}

  // L5：终极兜底 — 逐对象提取（哪怕整体残缺，也尽可能救回每个完整对象）
  const fragments = extractIndividualObjects(extracted);
  if (fragments.length > 0) {
    // 如果原始期望是一个数组，直接返回对象数组
    if (opts.expectArrayKey) {
      return { ok: true, data: fragments, fragments, strategy: 'object-fallback' };
    }
    // 否则尝试把对象们合并成一个对象
    const merged = fragments.reduce((acc, o) => ({ ...acc, ...o }), {});
    return { ok: true, data: merged, fragments, strategy: 'object-fallback' };
  }

  return { ok: false, data: null, fragments: [], strategy: 'all-failed' };
}

module.exports = {
  extractBalancedJson,
  fixTrailingCommas,
  fixUnescapedControlChars,
  closeUnclosedBrackets,
  extractIndividualObjects,
  robustJsonParse,
};
